// What the guides for a snap show (CO-5858): each face a face snap used, on
// the product snapped to and the dragged product, highlighted in place, a
// wall's apart so it can be drawn behind what stands in front of it; a
// marker on each of the two corners that met, with stubs along its own
// product's edges; and the outline of the product a product rule snapped
// to. DragGuides draws them.
import * as THREE from 'three';
import {AXES, axisIndex} from 'components/customer/RoomPlanner/helpers/axes';
import type {
    LinedUp,
    SnapPlane,
} from 'components/customer/RoomPlanner/helpers/snapping/strategies/faceSnap';
import type {SurfaceFacts} from 'components/customer/RoomPlanner/helpers/snapping/types';
import {flattenBox} from 'components/customer/RoomPlanner/helpers/boxes';
import {
    FaceSource,
    facesInPlane,
    outlineOfFaces,
} from 'components/customer/RoomPlanner/helpers/movement/planeFaces';
import {
    cornersOf,
    HeldCorner,
} from 'components/customer/RoomPlanner/helpers/snapping/strategies/cornerSnap';
import {WallFacts} from 'components/customer/RoomPlanner/helpers/snapping/roomWalls';

// How opaque a highlighted face's fill is; the faces snapping are outlined.
export const SNAPPED_FACE_OPACITY = 0.3;

// Faces this close (2.5 mm) are the same face.
const IN_PLANE = 0.5;

// The longest a corner's stub runs along an edge (75 mm), and the most of
// the box's side it may cover, so a thin product's stubs stay on it.
export const CORNER_STUB_LENGTH = 15;
const CORNER_STUB_SHARE = 0.45;

// Corners closer than this (0.5 mm) are the same corner.
const SAME_CORNER = 0.1;

/**
 * A marker on one of the two corners that met: the other product's or the
 * moving product's, where it is, and its stubs along its own edges as offsets
 * from it.
 */
export interface CornerMarker {
    kind: 'other' | 'moving';
    at: THREE.Vector3;
    stubs: THREE.Vector3[];
}

/**
 * A highlight: each face's four corners, in order around it, to fill, and
 * one outline round each plane's faces together (outlineOfFaces), as pairs
 * of ends.
 */
export interface FaceHighlight {
    fills: THREE.Vector3[][];
    outline: THREE.Vector3[][];
}

/**
 * The faces a face snap used, each face on its own: for every plane it used
 * (the face it met, and each end it lined up with), the other product's
 * components' faces, those of a wall an end lined up with, and the dragged
 * product's where it landed. Nothing else in the plane shows; that is the
 * line-up guide's to draw. A plane no face lies in is covered across the
 * other product.
 * @param {LinedUp[]} planes The planes the snap used, with what each lies on
 * when that isn't the other.
 * @param {SurfaceFacts} other The product or walls snapped to.
 * @param {FaceSource} moving The dragged product where it landed.
 * @return {object} The products' faces, and apart from them the room's
 * walls', which are drawn behind what stands in front of them.
 */
export const snappedFaces = (
    planes: readonly LinedUp[],
    other: SurfaceFacts,
    moving: FaceSource
): FaceHighlight & {walls: FaceHighlight} => {
    const products: FaceHighlight = {fills: [], outline: []};
    const walls: FaceHighlight = {fills: [], outline: []};
    planes.forEach((plane) => {
        const surfaces = plane.surface ? [other, plane.surface] : [other];
        const found: Record<'products' | 'walls', THREE.Box3[]> = {
            products: facesInPlane(moving, plane),
            walls: [],
        };
        surfaces.forEach((surface) =>
            found[surface instanceof WallFacts ? 'walls' : 'products'].push(
                ...facesInPlane(
                    {faces: surface.exteriorFaces, solids: surface.solidBBoxes},
                    plane
                )
            )
        );
        if (found.products.length + found.walls.length === 0) {
            found[other instanceof WallFacts ? 'walls' : 'products'].push(
                flattenBox(other.bbox, axisIndex(plane.axis), plane.position)
            );
        }
        (
            [
                [found.products, products],
                [found.walls, walls],
            ] as const
        ).forEach(([faces, highlight]) => {
            const snapping = distinct(faces);
            if (snapping.length === 0) return;
            highlight.fills.push(
                ...snapping.map((face) => rectCorners(face, plane))
            );
            highlight.outline.push(...outlineOfFaces(snapping, plane));
        });
    });

    return {...products, walls};
};

/**
 * @param {THREE.Box3[]} faces Faces, some perhaps found twice.
 * @return {THREE.Box3[]} Each face once.
 */
const distinct = (faces: THREE.Box3[]) =>
    faces.filter(
        (face, i) => faces.findIndex((first) => sameFace(first, face)) === i
    );

/**
 * @param {THREE.Box3} a A flat face.
 * @param {THREE.Box3} b Another.
 * @return {boolean} True when they are the same face, to within IN_PLANE.
 */
const sameFace = (a: THREE.Box3, b: THREE.Box3) =>
    a.min.distanceTo(b.min) < IN_PLANE && a.max.distanceTo(b.max) < IN_PLANE;

/**
 * @param {THREE.Box3} face A face, flat on the plane.
 * @param {SnapPlane} plane The plane.
 * @return {THREE.Vector3[]} Its four corners, in order around it, on the
 * plane.
 */
const rectCorners = (face: THREE.Box3, {axis, position}: SnapPlane) => {
    const index = axisIndex(axis);
    const [u, v] = AXES.filter((way) => way !== axis).map(axisIndex);
    const corner = (useMaxU: boolean, useMaxV: boolean) =>
        new THREE.Vector3()
            .setComponent(index, position)
            .setComponent(u, (useMaxU ? face.max : face.min).getComponent(u))
            .setComponent(v, (useMaxV ? face.max : face.min).getComponent(v));

    return [
        corner(false, false),
        corner(true, false),
        corner(true, true),
        corner(false, true),
    ];
};

/**
 * Short stubs from a corner along the edges of the box it belongs to.
 * @param {THREE.Box3} bbox The box.
 * @param {THREE.Vector3} corner One of its corners.
 * @return {THREE.Vector3[]} One stub per axis, x, y then z, as an offset
 * from the corner into the box.
 */
const cornerStubs = (
    bbox: THREE.Box3,
    corner: THREE.Vector3
): THREE.Vector3[] => {
    const centre = bbox.getCenter(new THREE.Vector3());
    const size = bbox.getSize(new THREE.Vector3());

    return AXES.map((axis) => {
        const index = axisIndex(axis);
        const inward =
            corner.getComponent(index) <= centre.getComponent(index) ? 1 : -1;

        return new THREE.Vector3().setComponent(
            index,
            inward *
                Math.min(
                    CORNER_STUB_LENGTH,
                    size.getComponent(index) * CORNER_STUB_SHARE
                )
        );
    });
};

/**
 * The markers for two corners that met: the other product's, with stubs
 * along the edges of its solid box that has the corner, and the moving
 * product's, with stubs along its own box.
 * @param {HeldCorner} held The corner snap: the other's corner, and which
 * corner of the moving product's box is on it.
 * @param {THREE.Box3} landed The moving product's box where it landed.
 * @param {SurfaceFacts} other The other product, or the walls.
 * @return {CornerMarker[]} The other's marker, then the moving product's.
 */
export const cornerMarkers = (
    held: HeldCorner,
    landed: THREE.Box3,
    other: SurfaceFacts
): CornerMarker[] => {
    const owner =
        other.solidBBoxes.find((solid) =>
            cornersOf(solid).some(
                (corner) => corner.distanceTo(held.vertex) < SAME_CORNER
            )
        ) ?? other.bbox;
    const moving = cornersOf(landed).at(held.corner) ?? held.vertex;

    return [
        {
            kind: 'other',
            at: held.vertex.clone(),
            stubs: cornerStubs(owner, held.vertex),
        },
        {kind: 'moving', at: moving, stubs: cornerStubs(landed, moving)},
    ];
};

/**
 * @param {THREE.Box3} bbox A product's box.
 * @return {Array<THREE.Vector3[]>} Its twelve edges, as pairs of ends.
 */
export const productOutline = (bbox: THREE.Box3): THREE.Vector3[][] => {
    const corners = cornersOf(bbox);

    return corners.flatMap((corner, i) =>
        [1, 2, 4]
            .filter((bit) => (i & bit) === 0)
            .map((bit) => [corner, corners[i | bit]])
    );
};
